import React, { useContext } from "react";
import { Form, Input, Button, Typography, message } from "antd";
import { MailOutlined, LockOutlined, ScheduleOutlined, EnvironmentOutlined, SafetyCertificateOutlined, ArrowRightOutlined } from "@ant-design/icons";
import { useNavigate, Link } from "react-router-dom";
import useRequest from "../../hooks/useRequest";
import apiPath from "../../constants/apiPath";
import { AuthContext } from "../../context/AuthContext";
import { requestPushSubscription, registerPushSubscription } from "../../helper/pushNotifications";

const { Title } = Typography;

const LogoMark = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="26" height="26" rx="7" fill="var(--i-ems-accent)" />
    <path d="M7.5 13.5l3.5 3.5 7.5-8" stroke="#0B1330" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LogoMarkLight = ({ size = 30 }) => (
  <svg width={size} height={size} viewBox="0 0 26 26" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect width="26" height="26" rx="7" fill="rgba(255,255,255,0.18)" stroke="rgba(255,255,255,0.3)" />
    <path d="M7.5 13.5l3.5 3.5 7.5-8" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const features = [
  {
    icon: <EnvironmentOutlined />,
    text: "Live geofenced attendance",
    sub: "Automatic check-in verification and exit alerts",
  },
  {
    icon: <ScheduleOutlined />,
    text: "Leave, WFH & holidays in one place",
    sub: "Approvals, balances and calendars stay in sync",
  },
  {
    icon: <SafetyCertificateOutlined />,
    text: "Full audit trail",
    sub: "Every sensitive action and access is logged",
  },
];

const Login = () => {
  const { request } = useRequest();
  const { setIsLoggedIn, setUserProfile } = useContext(AuthContext);
  const navigate = useNavigate();

  const onFinish = (values) => {
    // Started immediately on the Sign In click, in parallel with the login
    // request below — NOT chained after it resolves. A browser's "user
    // gesture" window is short enough that waiting for a full login
    // round-trip to the server before triggering the permission prompt
    // reliably misses it (confirmed: the popup never appeared automatically
    // that way). This only creates the browser-side subscription — no
    // backend call yet, since there's no auth token until login succeeds.
    const pushSubscriptionPromise =
      typeof Notification !== "undefined" && Notification.permission === "default"
        ? requestPushSubscription()
        : Promise.resolve(null);

    request({
      url: apiPath.login,
      method: "POST",
      data: values,
      onSuccess: (res) => {
        if (res?.status === 200) {
          localStorage.setItem("i_ems_admin_token", res.data.token);
          localStorage.setItem("i_ems_admin_profile", JSON.stringify(res.data.profile));
          setUserProfile(res.data.profile);
          setIsLoggedIn(true);
          message.success("Logged in successfully");
          navigate("/dashboard");
          // Now that a valid token exists, register whatever subscription
          // (if any) the permission prompt above resulted in.
          pushSubscriptionPromise.then((sub) => registerPushSubscription(request, sub));
        } else {
          message.error(res?.message || "Login failed");
        }
      },
      onError: (err) => {
        message.error(err?.response?.data?.message || "Login failed");
      },
    });
  };

  return (
    <div className="i-ems-login-page">
      <div className="i-ems-login-brand">
        <span className="i-ems-login-blob i-ems-login-blob-1" />
        <span className="i-ems-login-blob i-ems-login-blob-2" />
        <span className="i-ems-login-blob i-ems-login-blob-3" />

        <div className="i-ems-login-brand-top">
          <LogoMarkLight />
          <span className="i-ems-login-brand-wordmark">I-EMS Admin</span>
        </div>

        <div className="i-ems-login-brand-mid">
          <h1 className="i-ems-login-brand-headline">Run attendance &amp; HR with confidence.</h1>
          <p className="i-ems-login-brand-copy">
            One dashboard for employees, geofenced attendance, leave, payslips and reporting —
            built for teams that need accuracy and accountability.
          </p>
          <div className="i-ems-login-feature-list">
            {features.map((f) => (
              <div className="i-ems-login-feature" key={f.text}>
                <span className="i-ems-login-feature-icon">{f.icon}</span>
                <div>
                  <div className="i-ems-login-feature-text">{f.text}</div>
                  <div className="i-ems-login-feature-sub">{f.sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="i-ems-login-brand-footer">© {new Date().getFullYear()} Imergey · Employee Management Suite</div>
      </div>

      <div className="i-ems-login-form-panel">
        <span className="i-ems-login-form-glow-1" />
        <span className="i-ems-login-form-glow-2" />
        <div className="i-ems-login-card">
          <div className="i-ems-login-logo">
            <LogoMark />
            <span className="i-ems-login-logo-wordmark">I-EMS Admin</span>
          </div>
          <Title level={3} className="i-ems-login-title">
            Welcome back
          </Title>
          <div className="i-ems-login-subtitle">Sign in to manage your workforce</div>
          <Form layout="vertical" onFinish={onFinish}>
            <Form.Item name="email" label="Email" rules={[{ required: true, type: "email" }]}>
              <Input size="large" prefix={<MailOutlined style={{ color: "var(--i-ems-text-secondary)" }} />} placeholder="admin@company.com" />
            </Form.Item>
            <Form.Item
              name="password"
              label="Password"
              rules={[{ required: true, min: 6 }]}
              extra={
                <div style={{ textAlign: "right", marginTop: 4 }}>
                  <Link to="/forgot-password" style={{ fontSize: 12.5 }}>
                    Forgot password?
                  </Link>
                </div>
              }
            >
              <Input.Password size="large" prefix={<LockOutlined style={{ color: "var(--i-ems-text-secondary)" }} />} placeholder="••••••••" />
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button type="primary" htmlType="submit" size="large" block>
                Sign in <ArrowRightOutlined />
              </Button>
            </Form.Item>
          </Form>
          <div className="i-ems-login-footnote">Access is restricted to authorized HR &amp; admin accounts.</div>
        </div>
      </div>
    </div>
  );
};

export default Login;
